import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { useAppContext } from '../context/AppContext';
import toast from 'react-hot-toast';

// Schema de validação para edição de usuário
const editUserSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  email: z.string().email('Email inválido'),
  phone: z.string()
    .min(10, 'Telefone deve ter pelo menos 10 dígitos')
    .regex(/^\d+$/, 'Telefone deve conter apenas números'),
  role: z.enum(['user', 'owner', 'admin'], { 
    errorMap: () => ({ message: 'Selecione um cargo válido' }) 
  }),
  barbershopId: z.string().optional(),
});

const EditUserModal = ({ isOpen, onClose, user }) => {
  const { updateUserData, barbershops } = useAppContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      role: user?.role || 'user',
      barbershopId: user?.barbershopId || '',
    },
  });

  const selectedRole = watch('role');

  // Resetar form quando user mudar
  useEffect(() => {
    if (user) {
      reset({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'user',
        barbershopId: user.barbershopId || '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data) => {
    if (!user?.id) {
      toast.error('Erro: usuário inválido');
      return;
    }

    try {
      setIsSubmitting(true);

      // Preparar dados para atualização
      const updates = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        role: data.role,
      };

      // Se o role é owner, incluir barbershopId
      if (data.role === 'owner' && data.barbershopId) {
        updates.barbershopId = data.barbershopId;
      } else if (data.role !== 'owner') {
        // Se não é owner, remover barbershopId
        updates.barbershopId = null;
      }

      const result = await updateUserData(user.id, updates);

      if (result.success) {
        onClose();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erro ao atualizar usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar Usuário">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Nome */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome Completo *
          </label>
          <input
            type="text"
            {...register('name')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="João Silva"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            {...register('email')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="joao@email.com"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Telefone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Telefone *
          </label>
          <input
            type="tel"
            {...register('phone')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="11987654321"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
          )}
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cargo *
          </label>
          <select
            {...register('role')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="user">Usuário</option>
            <option value="owner">Dono de Barbearia</option>
            <option value="admin">Administrador</option>
          </select>
          {errors.role && (
            <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>
          )}
        </div>

        {/* Barbershop - apenas se role for owner */}
        {selectedRole === 'owner' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Barbearia *
            </label>
            <select
              {...register('barbershopId')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione uma barbearia</option>
              {barbershops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
            {errors.barbershopId && (
              <p className="text-red-500 text-sm mt-1">
                {errors.barbershopId.message}
              </p>
            )}
          </div>
        )}

        {/* Botões */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditUserModal;
